<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto" @click.self="$emit('close')" @keyup.esc="$emit('close')" tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="modal-title">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 id="modal-title" class="text-lg leading-6 font-medium text-gray-900 mb-4">
                Edit User
              </h3>

              <!-- User Details Form -->
              <div class="space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      v-model="formData.firstName"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      :class="{ 'border-red-500': errors.firstName }"
                    />
                    <p v-if="errors.firstName" class="text-red-500 text-xs mt-1">{{ errors.firstName }}</p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      v-model="formData.lastName"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      :class="{ 'border-red-500': errors.lastName }"
                    />
                    <p v-if="errors.lastName" class="text-red-500 text-xs mt-1">{{ errors.lastName }}</p>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    v-model="formData.emailAddress"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    :class="{ 'border-red-500': errors.emailAddress }"
                  />
                  <p v-if="errors.emailAddress" class="text-red-500 text-xs mt-1">{{ errors.emailAddress }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    v-model="formData.status"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="VERIFIED">Verified</option>
                    <option value="NOT_VERIFIED">Not Verified</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            @click="saveUser"
            :disabled="loading"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div v-if="loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            {{ loading ? 'Saving...' : 'Save Changes' }}
          </button>
          <button
            @click="$emit('close')"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref, reactive, onMounted, watch,
} from 'vue';
import { useUserStore } from '@/store/users';
import { useNotificationStore } from '@/store/notifications';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: { String: string };
  status: string;
}

const props = defineProps<{
  show: boolean;
  user: User;
}>();

const emit = defineEmits<{(e: 'close'): void;
  (e: 'userUpdated'): void;
}>();

const userStore = useUserStore();
const notificationStore = useNotificationStore();

const loading = ref(false);
const errors = reactive({
  firstName: '',
  lastName: '',
  emailAddress: '',
});

const formData = reactive({
  firstName: '',
  lastName: '',
  emailAddress: '',
  status: '',
});

function validateForm() {
  let isValid = true;

  if (!formData.firstName.trim()) {
    errors.firstName = 'First name is required';
    isValid = false;
  } else {
    errors.firstName = '';
  }

  if (!formData.lastName.trim()) {
    errors.lastName = 'Last name is required';
    isValid = false;
  } else {
    errors.lastName = '';
  }

  if (!formData.emailAddress.trim()) {
    errors.emailAddress = 'Email address is required';
    isValid = false;
  } else {
    errors.emailAddress = '';
  }

  return isValid;
}

async function saveUser() {
  if (!validateForm()) return;

  loading.value = true;
  try {
    await userStore.updateUser(
      props.user.id,
      formData.firstName,
      formData.lastName,
      formData.emailAddress,
      formData.status,
    );

    if (userStore.error) {
      notificationStore.showError('Update Failed', userStore.error);
    } else {
      notificationStore.showSuccess('User Updated', 'User information has been updated successfully.');
      emit('userUpdated');
      emit('close');
    }
  } catch (err) {
    notificationStore.showError('Update Error', 'An unexpected error occurred while updating the user.');
  } finally {
    loading.value = false;
  }
}

watch(() => props.show, (newShow) => {
  if (newShow && props.user) {
    formData.firstName = props.user.firstName;
    formData.lastName = props.user.lastName;
    formData.emailAddress = props.user.emailAddress.String;
    formData.status = props.user.status;
  }
});

onMounted(() => {
  if (props.show && props.user) {
    formData.firstName = props.user.firstName;
    formData.lastName = props.user.lastName;
    formData.emailAddress = props.user.emailAddress.String;
    formData.status = props.user.status;
  }
});
</script>
